import axios from "axios";
import { API_CONFIG, API_ENDPOINTS } from "../config/api";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "./auth";

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Access tokens live 15 minutes and refresh tokens rotate on every use (the
// old one is blacklisted). On a 401 we refresh once, sharing a single
// in-flight refresh across any requests that 401 at the same time, then
// retry the original request with the new access token.
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isRefreshCall = config?.url === API_ENDPOINTS.USER.TOKEN_REFRESH;

    if (response?.status === 401 && !config._retried && !isRefreshCall && getRefreshToken()) {
      config._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.USER.TOKEN_REFRESH}`, {
              refresh: getRefreshToken(),
            })
            .then(({ data }) => {
              setTokens(data.access, data.refresh);
              return data.access;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }
        const access = await refreshPromise;
        config.headers.Authorization = `Bearer ${access}`;
        return api(config);
      } catch (refreshError) {
        clearTokens();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// DRF paginates most list endpoints ({count, next, previous, results}), but
// at least one environment (local) has returned a bare array for the same
// endpoint that production paginates. Accept either shape rather than
// hard-coding one and silently reading undefined in the other environment.
export function unwrapList(data) {
  return Array.isArray(data) ? data : data?.results ?? [];
}

// Cursor URLs are deliberately opaque, but DRF may return them with the API
// server's absolute origin. Swap only that origin for the current app so the
// request continues through the same-origin proxy without inspecting or
// rebuilding the cursor query string.
export function sameOriginApiUrl(url) {
  if (!url) return null;
  const target = new URL(url, window.location.origin);
  target.protocol = window.location.protocol;
  target.host = window.location.host;
  return target.toString();
}

// Follows DRF's `next` cursor until exhausted, for callers that need the
// *complete* result set — unwrapList alone only sees whichever single page
// the server returned, which silently drops rows past page 1. `next` comes
// back as an absolute URL pointing at the backend's own origin, which (per
// BASE_URL above) has no CORS headers for the browser to call directly. The
// only thing that actually needs to change is the origin — swapped for this
// page's own, via the platform URL parser rather than string surgery, so
// the path, query string, and cursor reach the proxy exactly as the backend
// built them. The result is handed to axios as a fully-qualified absolute
// URL specifically so it bypasses baseURL-merging entirely for that call —
// no assumption about what prefix BASE_URL does or doesn't already carry.
export async function fetchAllPages(url, config) {
  let results = [];
  let nextUrl = url;
  let nextConfig = config;
  while (nextUrl) {
    const { data } = await api.get(nextUrl, nextConfig);
    if (Array.isArray(data)) return results.concat(data);
    results = results.concat(data.results ?? []);
    if (!data.next) break;
    nextUrl = sameOriginApiUrl(data.next);
    nextConfig = undefined;
  }
  return results;
}

export default api;
